<template>
  <div>
    <context-bar />

    <!-- calculation details (main report only) -->
    <v-card v-if="state.ctx === 'main'" outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        Calculation details
        <v-spacer />
        <v-btn small text @click="addInfo"><v-icon left small>mdi-plus</v-icon>Add detail</v-btn>
      </v-card-title>
      <v-card-text>
        <div class="text-caption grey--text mb-2">
          Facts people need to read the numbers correctly (e.g. the tax rate used). Shown under the filters and included in every export and print.
          Each is a query returning one row with a <code>value</code> column.
        </div>
        <v-card v-for="(inf, i) in tpl.info" :key="i" outlined class="mb-2">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="5"><v-text-field v-model="inf.label" outlined dense hide-details label="Label" /></v-col>
              <v-col cols="6" sm="3"><v-select v-model="inf.format" :items="formats" clearable outlined dense hide-details label="Format" /></v-col>
              <v-col cols="6" sm="3"><v-text-field v-model="inf.note" outlined dense hide-details label="Explanation (tooltip)" /></v-col>
              <v-col cols="auto"><v-btn icon small @click="tpl.info.splice(i, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn></v-col>
            </v-row>
            <code-box v-model="inf.sql" mode="sql" :rows="3" class="mt-2" />
          </v-card-text>
        </v-card>
        <div v-if="!tpl.info.length" class="text-caption grey--text">None.</div>
      </v-card-text>
    </v-card>

    <!-- exports -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        Exports
        <v-spacer />
        <v-btn small text @click="addExport"><v-icon left small>mdi-plus</v-icon>Add export</v-btn>
      </v-card-title>
      <v-card-text>
        <div class="text-caption grey--text mb-2">
          Each export is a CSV, Excel or JSON file of one dataset. By default it uses the table's column headings and includes a details block
          (filters used, calculation details, who generated it and when).
        </div>
        <v-card v-for="(e, i) in c.exports" :key="i" outlined class="mb-2">
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="3"><v-text-field v-model="e.name" outlined dense hide-details label="Name" /></v-col>
              <v-col cols="6" sm="2"><v-select v-model="e.type" :items="['csv', 'xlsx', 'json']" outlined dense hide-details label="File type" /></v-col>
              <v-col cols="6" sm="3"><v-select v-model="e.dataset" :items="datasetNames" outlined dense hide-details label="Dataset" /></v-col>
              <v-col cols="6" sm="3"><v-checkbox :input-value="e.details !== false" dense hide-details class="mt-0" label="Include filters & details" @change="e.details = $event" /></v-col>
              <v-col cols="auto"><v-btn icon small @click="c.exports.splice(i, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn></v-col>
            </v-row>
            <div class="mt-2">
              <v-checkbox :input-value="!!e.columns" dense hide-details class="mt-0" label="Choose the columns myself" @change="customColumns(e, $event)" />
              <div v-if="e.columns">
                <v-row v-for="(col, k) in e.columns" :key="k" dense align="center">
                  <v-col cols="5"><field-picker v-model="col.field" :dataset="e.dataset" label="Column" /></v-col>
                  <v-col cols="5"><v-text-field v-model="col.label" outlined dense hide-details label="Heading" /></v-col>
                  <v-col cols="2"><v-btn icon x-small @click="e.columns.splice(k, 1)"><v-icon small>mdi-close</v-icon></v-btn></v-col>
                </v-row>
                <v-btn x-small text @click="e.columns.push({ field: '', label: '' })"><v-icon left x-small>mdi-plus</v-icon>Add column</v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
        <div v-if="!c.exports.length" class="text-caption grey--text">None - the Export button will not appear.</div>
      </v-card-text>
    </v-card>

    <!-- prints -->
    <v-card outlined>
      <v-card-title class="text-subtitle-1">
        Print layouts
        <v-spacer />
        <v-btn small text @click="addPrint"><v-icon left small>mdi-plus</v-icon>Add print layout</v-btn>
      </v-card-title>
      <v-card-text>
        <v-card v-for="(p, i) in c.prints" :key="i" outlined class="mb-2">
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="3"><v-text-field v-model="p.name" outlined dense hide-details label="Name" /></v-col>
              <v-col cols="6" sm="2"><v-select v-model="p.paper" :items="['A4', 'A5', 'Letter', 'Legal', '80mm']" outlined dense hide-details label="Paper" /></v-col>
              <v-col cols="6" sm="2"><v-select v-model="p.orientation" :items="['portrait', 'landscape']" clearable outlined dense hide-details label="Orientation" /></v-col>
              <v-col cols="12" sm="4"><v-select v-model="p.sections" :items="sectionItems" multiple chips small-chips deletable-chips outlined dense hide-details label="Sections to print (empty = all)" /></v-col>
              <v-col cols="auto">
                <v-btn small color="primary" class="mr-1" @click="openDesigner(i)"><v-icon left small>mdi-palette-outline</v-icon>Design</v-btn>
                <v-btn icon small @click="c.prints.splice(i, 1)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-row dense class="mt-1">
              <v-col v-for="h in headerToggles" :key="h.key" cols="6" sm="3">
                <v-checkbox :input-value="header(p)[h.key] !== false" dense hide-details class="mt-0" :label="h.label" @change="setHeader(p, h.key, $event)" />
              </v-col>
            </v-row>
            <v-row dense class="mt-1">
              <v-col cols="12" sm="8"><v-text-field :value="(p.footer || {}).text" outlined dense hide-details label="Footer text (e.g. a signature line)" @input="setFooter(p, $event)" /></v-col>
            </v-row>
            <v-btn x-small text class="mt-1" @click="toggleCss(p)">{{ p.css === undefined ? 'Add' : 'Remove' }} custom print styles (CSS)</v-btn>
            <code-box v-if="p.css !== undefined" v-model="p.css" mode="js" :rows="4" />
          </v-card-text>
        </v-card>
        <div v-if="!c.prints.length" class="text-caption grey--text">None - a plain default layout is used for the Print button.</div>
        <div class="text-caption grey--text mt-2">
          Prints always list the filters used and the run information unless you untick them here. Press <b>Design</b> for a live paper preview with margins, text size, section order and page breaks.
        </div>
      </v-card-text>
    </v-card>
    <print-designer />
  </div>
</template>

<script>
import CodeBox from './CodeBox.vue'
import ContextBar from './ContextBar.vue'
import FieldPicker from './FieldPicker.vue'
import PrintDesigner from './PrintDesigner.vue'
import { state, container, datasetNames, FORMATS } from './editorState'

export default {
  name: 'RbOutputTab',
  components: { CodeBox, ContextBar, FieldPicker, PrintDesigner },
  data () {
    return {
      state,
      formats: FORMATS,
      headerToggles: [
        { key: 'title', label: 'Report title' }, { key: 'params', label: 'Filters used' },
        { key: 'info', label: 'Calculation details' }, { key: 'runInfo', label: 'Run information (who / when)' }
      ]
    }
  },
  computed: {
    tpl () { return state.tpl },
    c () {
      const c = container()
      if (!c.exports) this.$set(c, 'exports', [])
      if (!c.prints) this.$set(c, 'prints', [])
      return c
    },
    datasetNames () { return datasetNames() },
    sectionItems () { return container().ui.sections.map((s) => ({ text: s.title || s.type, value: s.id })) }
  },
  methods: {
    openDesigner (i) {
      state.designer.index = i
      state.designer.open = true
    },
    addInfo () {
      this.tpl.info.push({ label: 'Tax rate (VAT) in system settings', format: 'percent', sql: 'SELECT ROUND(:sys_vatRate * 100, 2) AS value', note: '' })
    },
    addExport () {
      this.c.exports.push({ name: 'Export', type: 'csv', dataset: this.datasetNames[0], details: true })
    },
    customColumns (e, on) {
      if (on) this.$set(e, 'columns', [{ field: '', label: '' }])
      else this.$delete(e, 'columns')
    },
    addPrint () {
      this.c.prints.push({ name: 'Print', paper: 'A4', orientation: 'portrait', header: { title: true, params: true, info: true, runInfo: true }, footer: { pageNumbers: true } })
    },
    header (p) { return p.header || {} },
    setHeader (p, key, on) {
      if (!p.header) this.$set(p, 'header', {})
      this.$set(p.header, key, on)
    },
    setFooter (p, text) {
      if (!p.footer) this.$set(p, 'footer', {})
      this.$set(p.footer, 'text', text)
    },
    toggleCss (p) {
      if (p.css === undefined) this.$set(p, 'css', '/* extra print styles */\n')
      else this.$delete(p, 'css')
    }
  }
}
</script>
